import { BookOpenCheck, Calculator, CheckCheck, ClipboardList, KeyRound, LayoutDashboard, ListChecks, LogOut, Menu, Settings2, ShieldAlert, UsersRound, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '../features/auth/AuthProvider';
import { Button } from '../components/ui';
import { pendingApprovalCount } from '../features/approvals/api';
import { roleLabels } from '../features/auth/permissions';
import { features, type FeatureName } from '../config/features';
import genproektLogo from '../assets/GENPROEKT_logo.png';

const primary = [
  { to: '/app/dashboard', label: 'Обзор', icon: LayoutDashboard },
  { to: '/app/calculator/new', label: 'Новый расчёт', icon: Calculator, create: true },
  { to: '/app/quotes', label: 'История', icon: ClipboardList },
  { to: '/app/approvals', label: 'Согласования', icon: CheckCheck },
];
const admin = [
  { to: '/app/admin/pricing', label: 'Управление прайсом', icon: Settings2, feature: 'priceAdministration' },
  { to: '/app/admin/review', label: 'Требует проверки', icon: ShieldAlert, feature: 'priceAdministration' },
  { to: '/app/admin/approvals', label: 'Маршруты согласования', icon: ListChecks, feature: 'approvalWorkflowAdministration' },
] satisfies Array<{ to: string; label: string; icon: typeof UsersRound; feature: FeatureName }>;

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { fullName, role, user, signOut, canManageAccounts } = useAuth();
  const { data: pendingApprovals = 0 } = useQuery({
    queryKey: ['approval-pending-count', user?.id, role],
    queryFn: () => pendingApprovalCount(user?.id ?? '', role),
    enabled: Boolean(user && role && role !== 'manager'),
    refetchInterval: 60_000,
  });
  const roleLinks = primary.filter((link) => !link.create || role !== 'approver');
  const links = [
    ...roleLinks,
    { to: '/app/instructions', label: 'Инструкция', icon: BookOpenCheck },
    { to: '/app/account', label: 'Мой аккаунт', icon: KeyRound },
    ...(canManageAccounts ? [{ to: '/app/admin/users', label: 'Пользователи', icon: UsersRound }] : []),
    ...((role === 'admin' || role === 'superadmin') ? [{ to: '/app/admin/prices', label: 'Цены справочника', icon: Settings2 }] : []),
    ...(canManageAccounts ? admin.filter((link) => features[link.feature]) : []),
  ];
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <button className="fixed left-4 top-4 z-50 grid size-12 place-items-center rounded-2xl bg-brand text-white shadow-soft lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Открыть меню"><Menu /></button>
      {mobileOpen ? <button className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Закрыть меню" /> : null}
      <aside className={clsx('fixed inset-y-0 left-0 z-50 flex w-[292px] flex-col border-r border-ink/10 bg-sand p-5 text-ink shadow-[18px_0_48px_rgba(34,37,38,0.06)] transition-transform lg:translate-x-0', mobileOpen ? 'translate-x-0' : '-translate-x-full')}>
        <div className="mb-10 px-2 pt-2">
          <div className="relative flex justify-center">
            <img src={genproektLogo} alt="GENPROEKT" className="h-auto w-[calc(100%_-_3.5rem)] max-w-[15rem] object-contain lg:w-full" />
            <button className="absolute right-0 top-0 grid size-10 place-items-center rounded-xl bg-white/70 text-ink lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Закрыть"><X /></button>
          </div>
          <div className="mt-5">
            <div className="font-display text-lg font-bold tracking-tight">Смета</div>
            <div className="mt-0.5 text-xs font-semibold tracking-[0.08em] text-brand">Конструктор КП</div>
          </div>
        </div>
        <nav className="grid gap-1.5">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => clsx(
                'flex min-h-12 items-center gap-3 rounded-2xl px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
                isActive
                  ? 'bg-white text-ink shadow-sm hover:bg-brand-soft hover:text-brand-dark'
                  : 'text-ink/60 hover:bg-white/70 hover:text-ink',
              )}
            >
              <Icon className="size-4.5" />{label}{to === '/app/approvals' && pendingApprovals > 0 ? <span className="ml-auto rounded-full bg-brand px-2 py-0.5 text-[10px] text-white">+{pendingApprovals}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto rounded-3xl border border-white/70 bg-white/55 p-4 shadow-sm">
          <div className="mb-4 flex items-center gap-3">
            <div className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-soft font-bold text-brand">{fullName.slice(0, 1).toUpperCase()}</div>
            <div className="min-w-0"><p className="truncate text-sm font-bold">{fullName}</p><p className="text-xs text-ink/45">{role ? roleLabels[role] : 'Сотрудник'}</p></div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-ink/60 hover:bg-white hover:text-ink" onClick={() => void signOut()}><LogOut className="size-4" />Выйти</Button>
        </div>
      </aside>
      <main className="min-h-screen px-4 pb-24 pt-4 sm:px-7 lg:ml-[292px] lg:px-10 lg:py-9 xl:px-14">
        <div className="mx-auto max-w-[1500px]"><Outlet /></div>
      </main>
    </div>
  );
}
